import { useState, useEffect } from "react";
import styles from "./list.module.css";
import { TextField, Button, Table, TableCell, TableHead, TableContainer, Paper, TableBody, TableRow, TableFooter, Dialog, Snackbar, Alert, Switch, Tooltip } from "@mui/material";
import { StyledMainTableCell, StyledMainTableRow } from "../../components/TableComponent";
import TablePaginationComponent from "../../components/TablePagination";
import { Add, Close } from "@mui/icons-material";
import axios from "axios";
import DeletePopup from "../../components/DeletePopup";
import Loader from "../../components/Loader";
import CustomerListLoader from "./CustomerListLoader";
import { authData, withAdminAuth } from "../../components/GetAuth";
import { Edit, Lock, Plus, Search, Trash2 } from "react-feather";
import dayjs from "dayjs";
import PageHeader from "../../components/PageHeader";
import { useWebsite } from "../../../context/WebsiteContext";
import { useSiteBranding } from "../../../hooks/useSiteBranding";
import ChangePasswordForm from "../../components/popups/ChangePasswordForm";
import AddCustomerForm from "../../components/popups/AddCustomerForm";
import AddmembershipCustomerForm from "../../components/popups/AddmembershipCustomerForm";

const CustomerList = () => {
  const auth = authData();
  const API_TOKEN = auth?.api_token;
  const [loading, setLoading] = useState(false);
  const [contentLoading, setContentLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filteredData, setFilteredData] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [filterVars, setFilterVars] = useState({ searchKey: "" });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState("success");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(0);
  const [editData, setEditData] = useState(null);
  const [addMembershipModalShow, setAddMembershipModalShow] = useState(false);
  const [currentItemId, setCurrentItemId] = useState(0);
  const [membershipList, setMembershipList] = useState([]);
  const [formOpenPassword, setFormOpenPassword] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const { websiteData } = useWebsite();
  useSiteBranding("Customer list", websiteData);

  const canUpdate = auth?.user_type === 2 || auth?.permissions?.indexOf('customers:update') > -1;

  const paginationChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const paginationChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    fetchCustomers();
  }, [page, rowsPerPage]);

  useEffect(() => {
    if (page === 0) {
      fetchCustomers();
    } else {
      setPage(0);
    }
  }, [filterVars]);

  const fetchCustomers = () => {
    axios.get(process.env.REACT_APP_API_URL + "v1/customers", {
      headers: {
        Authorization: `Bearer ${API_TOKEN}`,
      },
      params: {
        page: page + 1,
        per_page: rowsPerPage,
        search: filterVars.searchKey
      }
    }).then((response) => {
      setContentLoading(false);

      setFilteredData(response.data.data.data);
      setTotalCount(response.data.data.total);
    }).catch((error) => {
      setContentLoading(false);
    });
  }

  useEffect(() => {
    axios
      .get(process.env.REACT_APP_API_URL + "v1/membership-list", {
        headers: {
          Authorization: `Bearer ${API_TOKEN}`,
        },
      })
      .then((response) => {
        if (response.data.status === "SUCCESS") {
          let itemData = response.data.data;
          setMembershipList(itemData);
        }
      });
  }, [API_TOKEN]);

  const deleteConfirm = () => {
    setLoading(true);
    setDeleteModalOpen(false);
    axios.delete(process.env.REACT_APP_API_URL + "v1/customers/" + deleteId, {
      headers: {
        Authorization: `Bearer ${API_TOKEN}`,
      }
    }).then((response) => {
      setLoading(false);
      if (response.data.status === "SUCCESS") {
        setSnackbarOpen(true);
        setSnackbarMessage(response.data.message);
        setSnackbarType("success");

        fetchCustomers();

        setDeleteId(0);
      }
      if (response.data.status === "ERROR") {
        setSnackbarOpen(true);
        setSnackbarMessage(response.data.message);
        setSnackbarType("error");
      }
    }).catch((error) => {
      setLoading(false);
      if (error && error.response && error.response.data && error.response.data.messages) {
        setSnackbarOpen(true);
        setSnackbarMessage(error.response.data.messages);
        setSnackbarType("error");
      }
    });
  };

  const statusChange = (e, item) => {
    let status = 0;
    if (e.target.checked) {
      status = 1;
    }
    setLoading(true);
    axios.put(process.env.REACT_APP_API_URL + "v1/customers/" + item?.id, { status: status }, {
      headers: {
        Authorization: `Bearer ${API_TOKEN}`,
      }
    }).then((response) => {
      setLoading(false);
      if (response.data.status === "SUCCESS") {
        setSnackbarOpen(true);
        setSnackbarMessage(response.data.message);
        setSnackbarType("success");

        fetchCustomers();
      }
      if (response.data.status === "ERROR") {
        setSnackbarOpen(true);
        setSnackbarMessage(response.data.message);
        setSnackbarType("error");
      }
    })
      .catch((error) => {
        setLoading(false);
        if (error && error.response && error.response.data && error.response.data.messages) {
          setSnackbarOpen(true);
          setSnackbarMessage(error.response.data.messages);
          setSnackbarType("error");
        }
      });
  }

  const onSubmitAddForm = (data) => {
    setPage(0);
    fetchCustomers();
  }

  const onSubmitEditForm = (data) => {
    fetchCustomers();
  }

  const renderActions = (item) => (<>
    <Button onClick={(e) => { setFormOpen(true); setEditData(item); }} className={`${styles.TableActionBU}`} aria-label="Edit customer"><Edit /></Button>
    <Button onClick={(e) => { setFormOpenPassword(true); setEditData(item); }} className={`${styles.TableActionBU}`} aria-label="Change password"><Lock /></Button>
    <Button onClick={(e) => { setDeleteModalOpen(true); setDeleteId(item?.id); }} className={`${styles.TableActionBU}`} aria-label="Delete customer"><Trash2 /></Button>
    {membershipList.length > 0 && auth?.subscription?.id > 3 && <Tooltip title="Add Membership">
      <Button onClick={(e) => { setAddMembershipModalShow(true); setCurrentItemId(item?.id); }} className={`${styles.TableActionBU}`} aria-label="Add membership"><Plus /></Button>
    </Tooltip>}
  </>);

  return (<>
    <Loader open={loading} setOpen={setLoading.bind(this)} />
    <div className={`${styles.MainDiv}`}>
      <PageHeader title="Customers" trail={[{ label: "List" }]} />

      <div className={`${styles.MainCard}`}>

        {contentLoading && <CustomerListLoader />}

        {!contentLoading && (<>
          <div className={styles.TableFilterHead}>
            <div className={styles.SearchWrap}>
              <Search className={styles.SearchIcon} />
              <TextField
                variant="standard"
                type="text"
                placeholder="Search picked items"
                value={filterVars?.searchKey}
                onChange={(e) => setFilterVars((prev) => ({ ...prev, searchKey: e.target.value }))}
                className={styles.SearchInput}
                InputProps={{ disableUnderline: true }}
                fullWidth
              />
              {filterVars?.searchKey !== "" && (
                <Button type="button" className={styles.ClearBU} onClick={(e) => setFilterVars((prev) => ({ ...prev, searchKey: "" }))}>
                  <Close />
                </Button>
              )}
            </div>

            {(auth?.user_type === 2 || auth?.permissions?.indexOf('customers:add') > -1) && <div className={`${styles.TableFilterBUSec}`}>
              <Button className={`${styles.TableAddBU}`} onClick={(e) => { setFormOpen(true); setEditData(null); }}><Add /> Customer</Button>
            </div>}
          </div>

          {/* Desktop table */}
          <div className={`${styles.CusTable}`}>
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} aria-label="Customer list table">
                <TableHead>
                  <TableRow>
                    <StyledMainTableCell>Customer Id</StyledMainTableCell>
                    <StyledMainTableCell>Name</StyledMainTableCell>
                    <StyledMainTableCell>Email</StyledMainTableCell>
                    <StyledMainTableCell>Phone</StyledMainTableCell>
                    <StyledMainTableCell>Current Membership</StyledMainTableCell>
                    <StyledMainTableCell>Active</StyledMainTableCell>
                    {canUpdate && <StyledMainTableCell></StyledMainTableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData.map((item, index) => {
                    return (<StyledMainTableRow key={index} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                      <TableCell component="th" scope="row">{item?.customer_id}</TableCell>
                      <TableCell component="th" scope="row">{item?.name}</TableCell>
                      <TableCell component="th" scope="row">{item?.email}</TableCell>
                      <TableCell component="th" scope="row">{item?.phone}</TableCell>
                      <TableCell component="th" scope="row">
                        <span className={`${styles.MembershipCell}`}>{item.current_membership ? item?.current_membership : "-"}</span>
                        {item.membership_expired_date && <span className={`${styles.MembershipExpiry}`}>Expired On: {dayjs(item?.membership_expired_date).format("DD/MM/YYYY")}</span>}
                      </TableCell>
                      <TableCell component="th" scope="row">
                        <Switch
                          disabled={!canUpdate}
                          checked={item?.status === 1 ? true : false}
                          onChange={(e) => statusChange(e, item)}
                          inputProps={{ "aria-label": "controlled" }}
                          className={styles.CustomSwitch}
                        />
                      </TableCell>
                      {canUpdate && <TableCell component="th" scope="row" align="right">
                        {renderActions(item)}
                      </TableCell>}
                    </StyledMainTableRow>);
                  })}
                  {filteredData.length === 0 && (<TableRow>
                    <TableCell colSpan={6} align="center">No data found</TableCell>
                  </TableRow>)}
                </TableBody>
                {filteredData.length > 0 && (<TableFooter>
                  <TableRow>
                    <TablePaginationComponent
                      count={totalCount}
                      colSpan={6}
                      page={page}
                      rowsPerPage={rowsPerPage}
                      handleChangePage={paginationChangePage}
                      handleChangeRowsPerPage={
                        paginationChangeRowsPerPage
                      }
                    />
                  </TableRow>
                </TableFooter>)}
              </Table>
            </TableContainer>
          </div>

          {/* Mobile stacked cards — shown instead of the table below 768px */}
          <div className={`${styles.MobileCardList}`}>
            {filteredData.map((item, index) => (
              <div className={`${styles.CustomerCard}`} key={index}>
                <div className={`${styles.CustomerCardHead}`}>
                  <div>
                    <p className={`${styles.CustomerCardName}`}>{item?.name}</p>
                    <p className={`${styles.CustomerCardMeta}`}>#{item?.customer_id}</p>
                  </div>
                  <Switch disabled={!canUpdate} checked={item?.status === 1 ? true : false} onChange={(e) => statusChange(e, item)} inputProps={{ "aria-label": "controlled" }} />
                </div>
                <div className={`${styles.CustomerCardBody}`}>
                  <p><span>Email</span>{item?.email}</p>
                  <p><span>Phone</span>{item?.phone}</p>
                  <p><span>Membership</span>{item.current_membership ? item?.current_membership : "-"}{item.membership_expired_date && ` (exp. ${dayjs(item?.membership_expired_date).format("DD/MM/YYYY")})`}</p>
                </div>
                {canUpdate && <div className={`${styles.CustomerCardActions}`}>
                  {renderActions(item)}
                </div>}
              </div>
            ))}
            {filteredData.length === 0 && <div className={`${styles.NoDataCard}`}>No data found</div>}
            {filteredData.length > 0 && (
              <TablePaginationComponent
                count={totalCount}
                colSpan={6}
                page={page}
                rowsPerPage={rowsPerPage}
                handleChangePage={paginationChangePage}
                handleChangeRowsPerPage={paginationChangeRowsPerPage}
              />
            )}
          </div>
        </>)}
      </div>
    </div>

    <Dialog open={formOpen} onClose={(e) => setFormOpen(false)} fullWidth maxWidth="sm">
      <AddCustomerForm onSubmitAddForm={onSubmitAddForm.bind(this)} onSubmitEditForm={onSubmitEditForm.bind(this)} setFormOpen={setFormOpen.bind(this)} setSnackbarOpen={setSnackbarOpen.bind(this)} setSnackbarMessage={setSnackbarMessage.bind(this)} setSnackbarType={setSnackbarType.bind(this)} setLoading={setLoading.bind(this)} editData={editData} />
    </Dialog>

    <DeletePopup open={deleteModalOpen} setOpen={setDeleteModalOpen.bind(this)} confirmAction={deleteConfirm.bind(this)} />

    <Dialog open={addMembershipModalShow} onClose={(e) => setAddMembershipModalShow(false)} fullWidth maxWidth="sm">
      <AddmembershipCustomerForm id={currentItemId} setAddMembershipModalShow={setAddMembershipModalShow.bind(this)} setAllData={setFilteredData.bind(this)} setSnackbarOpen={setSnackbarOpen.bind(this)} setSnackbarMessage={setSnackbarMessage.bind(this)} setSnackbarType={setSnackbarType.bind(this)} setLoading={setLoading.bind(this)} editData={editData} />
    </Dialog>

    <Dialog open={formOpenPassword} onClose={(e) => setFormOpenPassword(false)} fullWidth maxWidth="sm">
      <ChangePasswordForm setFormOpen={setFormOpenPassword.bind(this)} setSnackbarOpen={setSnackbarOpen.bind(this)} setSnackbarMessage={setSnackbarMessage.bind(this)} setSnackbarType={setSnackbarType.bind(this)} setLoading={setLoading.bind(this)} id={editData?.user_id} />
    </Dialog>

    <Snackbar
      open={snackbarOpen}
      autoHideDuration={6000}
      onClose={(e) => setSnackbarOpen(false)}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Alert
        onClose={(e) => setSnackbarOpen(false)}
        severity={snackbarType}
        variant="filled"
        sx={{ width: "100%" }}
      >
        {snackbarMessage}
      </Alert>
    </Snackbar>
  </>
  );
};

export default withAdminAuth(CustomerList);